import { useState, useEffect, useRef, useCallback, type Ref } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getInspectionId,
  getDisplayedDifferenceGroups,
  getRepeatsPerPage,
  getFocusedDifference,
  inspection,
  app,
  getAllAnnotationsLoaded,
  getFocusedMatchIndex,
  getStepAndRepeatListItems,
  ListItemType,
  findRepeatScrollIndex,
  getSelectedModule,
} from 'store';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { getStyleVariables } from 'styles/vars';
import { Grid, Theme } from '@mui/material';
import { GVTypography } from 'components/lib';
import DifferenceElement from './DifferenceElement';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@emotion/react';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { Difference } from 'types';
import { CtfMatchResultWithDifferences } from 'store/request/match/types';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { DocumentTypes } from 'types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { mapDifferenceNumberToListIndex } from './diffListNavUtils';
import { useDifferenceListInteraction } from './useDifferenceListInteraction';
import {
  DifferenceListLoading,
  DifferenceListZeroResultsHeader,
  useDifferenceListSharedStyles,
} from './DifferenceListShared';

const useStyles = makeStyles()((theme: Theme) => {
  return {
    subheadingContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1.375, 0.75, 0.75, 0.25),
      margin: theme.spacing(0, 2, 0, 2),
      borderRadius: theme.spacing(0.5),
      cursor: 'default',
    },
    subheadingText: {
      color: 'var(--lightgrey-100, #8792AB)',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    resultCountText: {
      color: 'var(--lightgrey-100, #8792AB)',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    noDifferencesContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(1, 0.5),
      margin: theme.spacing(0, 2, 0, 2),
      borderRadius: theme.spacing(0.5),
      background: ' var(--gds-color-cool-gray-500, #292B30)',
      cursor: 'pointer',
    },
    noDifferencesText: {
      color: '#FFFFFF',
      fontSize: '12px',
      fontWeight: 600,
      textAlign: 'center',
    },
    selected: {
      '&&': {
        borderLeft: `3px solid #E53935`,
      },
    },
    collapsibleHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1.375, 0.5, 0, 0.25),
      margin: theme.spacing(0, 2, 0, 2),
      borderRadius: theme.spacing(0.5),
      cursor: 'pointer',
    },
    collapsibleHeaderText: {
      color: 'var(--lightgrey-100, #8792AB)',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
    },
    chevronIcon: {
      color: '#FFFFFF',
      fontSize: '16px',
    },
  };
});

export interface StepAndRepeatListOfDifferencesProps {
  multipleSelectionMode?: boolean;
  selectedDifferenceIds?: string[];
  onDifferenceSelection?: (difference: Difference, checked: boolean) => void;
}

const StepAndRepeatListOfDifferences = ({
  multipleSelectionMode = false,
  selectedDifferenceIds = [],
  onDifferenceSelection = () => {},
}: StepAndRepeatListOfDifferencesProps) => {
  const { classes: sharedClasses } = useDifferenceListSharedStyles();
  const { classes } = useStyles();
  const theme = useTheme();
  const styleVariables = getStyleVariables(theme);
  const dispatch = useDispatch();
  const flags = useFlags();

  const differenceListUnsorted = useSelector(getDisplayedDifferenceGroups);
  const inspectionId = useSelector(getInspectionId);
  const {
    data: inspectionCustomStatuses,
    isLoading: customStatusesLoading,
    refetch: refetchStatuses,
  } = useInspectionCustomStatuses(inspectionId);
  const repeatsPerPage = useSelector(getRepeatsPerPage);
  const focusedDifference = useSelector(getFocusedDifference);
  const focusedMatchIndex = useSelector(getFocusedMatchIndex);
  const currentDifferenceId = focusedDifference?.id;
  const currentDifferenceGroupId = focusedDifference?.groupId;
  const annotationsLoaded = useSelector(getAllAnnotationsLoaded);
  const selectedModule = useSelector(getSelectedModule);

  const [isNoDifferencesExpanded, setIsNoDifferencesExpanded] = useState(false);
  const [selectedNoDifferenceRepeatIndex, setSelectedNoDifferenceRepeatIndex] = useState<number | null>(null);

  const listItems = useSelector(getStepAndRepeatListItems(isNoDifferencesExpanded, inspectionCustomStatuses));

  const getDifferenceIndex = useCallback(() => {
    if (!currentDifferenceId && !currentDifferenceGroupId) {
      return -1;
    }
    return listItems.findIndex(
      (item) =>
        item.type === ListItemType.Difference &&
        (item.difference.id === currentDifferenceId ||
          (item.difference.groupId && item.difference.groupId === currentDifferenceGroupId)),
    );
  }, [currentDifferenceId, currentDifferenceGroupId, listItems]);

  const isNavigableIndex = useCallback(
    (index: number) => listItems[index]?.type === ListItemType.Difference,
    [listItems],
  );

  // Hotkeys pass numberOfDifference + 1 (card number), not Virtuoso index — map past headers/rows.
  const resolveHotkeyTargetToListIndex = useCallback(
    (differenceNumber: number) => mapDifferenceNumberToListIndex(listItems.length, differenceNumber, isNavigableIndex),
    [listItems.length, isNavigableIndex],
  );

  const onClickSelectExtra = useCallback(() => {
    setSelectedNoDifferenceRepeatIndex(null);
  }, []);

  const {
    virtuoso,
    currentItemIndex,
    clickSelect,
    setClickSelect,
    openComments,
    onClickSelect,
    getCardRef,
    updateCommentOpen,
    handleOnStopKeyboardNavigation,
    scrollerRef,
    enableKeyboardNavigation,
    resetKeyboardFocus,
  } = useDifferenceListInteraction({
    itemCount: listItems.length,
    getFocusedListIndex: getDifferenceIndex,
    resolveHotkeyTargetToListIndex,
    isNavigableIndex,
    onClickSelectExtra,
    initialCommentIds: differenceListUnsorted?.map((d) => d.id) ?? [],
  });

  useEffect(() => {
    if (currentDifferenceId || currentDifferenceGroupId) {
      setSelectedNoDifferenceRepeatIndex(null);
    }
  }, [currentDifferenceId, currentDifferenceGroupId]);

  const previousIndexRef = useRef<number>(-1);

  // Keep selection in view after a re-sort; skip while keyboard nav is active.
  useDeepCompareEffect(() => {
    if (!currentDifferenceId || clickSelect) return;
    if (flags?.enableKeyboardNavigation && currentItemIndex !== -1) return;

    const index = getDifferenceIndex();
    const previousIndex = previousIndexRef.current;

    if (previousIndex !== -1 && previousIndex !== index && index > -1 && virtuoso.current) {
      setTimeout(() => {
        if (virtuoso.current && getDifferenceIndex() > -1) {
          virtuoso.current.scrollToIndex({ index: getDifferenceIndex(), align: 'start', behavior: 'smooth' });
        }
      }, 100);
    }

    previousIndexRef.current = index;
    /* eslint-disable-next-line */
  }, [listItems, currentDifferenceId, clickSelect, flags?.enableKeyboardNavigation, currentItemIndex]);

  useEffect(() => {
    const index = getDifferenceIndex();
    const repeatListIndex = focusedMatchIndex !== -1 ? findRepeatScrollIndex(listItems, focusedMatchIndex) : -1;

    // Keyboard nav owns scrolling while a difference card is focused. Repeat-only
    // focus (empty repeat from the dropdown) must still auto-scroll.
    if (flags?.enableKeyboardNavigation && currentItemIndex !== -1 && index > -1) {
      return;
    }

    if (focusedMatchIndex !== -1) {
      const hasRepeatWithoutDifferences = Object.values(repeatsPerPage).some((pageRepeats) =>
        pageRepeats.some((repeat) => repeat.index === focusedMatchIndex && repeat.differences.length === 0),
      );

      if (hasRepeatWithoutDifferences) {
        setSelectedNoDifferenceRepeatIndex(focusedMatchIndex);
        resetKeyboardFocus();
        if (!isNoDifferencesExpanded) {
          setIsNoDifferencesExpanded(true);
          setClickSelect(false);
          return;
        }
      } else {
        setSelectedNoDifferenceRepeatIndex(null);
        if (index === -1) {
          resetKeyboardFocus();
        }
      }
    } else {
      setSelectedNoDifferenceRepeatIndex(null);
    }

    const scrollToListIndex = (targetIndex: number) => {
      requestAnimationFrame(() => {
        virtuoso.current?.scrollToIndex({ index: targetIndex, align: 'start', behavior: 'auto' });
      });
    };

    if (virtuoso.current !== null) {
      if (index > -1) {
        if (!clickSelect) {
          scrollToListIndex(index);
        } else {
          virtuoso.current.scrollIntoView({ index, behavior: 'smooth' });
        }
      } else if (repeatListIndex > -1) {
        scrollToListIndex(repeatListIndex);
      }
    }

    previousIndexRef.current = index;
    setClickSelect(false);
    /* eslint-disable-next-line */
  }, [currentDifferenceId, currentDifferenceGroupId, focusedMatchIndex, listItems.length, isNoDifferencesExpanded]);

  const handleToggleNoDifferences = () => {
    const newExpandedState = !isNoDifferencesExpanded;
    setIsNoDifferencesExpanded(newExpandedState);

    if (!newExpandedState && focusedMatchIndex !== -1) {
      let isFocusedMatchNoDifference = false;
      Object.keys(repeatsPerPage).forEach((pageNumber) => {
        const pageRepeats = repeatsPerPage[parseInt(pageNumber)];
        pageRepeats.forEach((repeat) => {
          if (repeat.index === focusedMatchIndex && repeat.differences.length === 0) {
            isFocusedMatchNoDifference = true;
          }
        });
      });

      if (isFocusedMatchNoDifference) {
        dispatch(app.actions.setFocusedMatchIndex(-1));
        setSelectedNoDifferenceRepeatIndex(null);

        const repeatAnnotation = PDFAnnotationManager.getInstance(DocumentTypes.target)?.getAnnotationById(
          `repeat-${focusedMatchIndex}`,
        );
        if (repeatAnnotation) {
          PDFAnnotationManager.repeatAnnotationDeselected(repeatAnnotation);
        }
      }
    }
  };

  const handleNoDifferenceClick = (
    repeat: CtfMatchResultWithDifferences & { differencesPostFiltering: Difference[] },
  ) => {
    if (focusedDifference) {
      dispatch(inspection.actions.unfocusDifference());
    }

    const repeatAnnotation = PDFAnnotationManager.getInstance(DocumentTypes.target)?.getAnnotationById(
      `repeat-${repeat.index}`,
    );

    if (!repeatAnnotation) return;

    if (focusedMatchIndex === repeat.index) {
      PDFAnnotationManager.repeatAnnotationDeselected(repeatAnnotation);
      setSelectedNoDifferenceRepeatIndex(null);
    } else {
      PDFAnnotationManager.repeatAnnotationSelected(repeatAnnotation);
      setSelectedNoDifferenceRepeatIndex(repeat.index);
    }
  };

  const hasZeroResults = listItems.length === 0;

  useEffect(() => {
    if (virtuoso.current && listItems.length > 0) {
      const shouldScrollToTop = getDifferenceIndex() === -1;
      if (shouldScrollToTop) {
        virtuoso.current.scrollToIndex({ index: 0, align: 'start', behavior: 'auto' });
      }
    }
  }, [selectedModule]);

  return !customStatusesLoading && annotationsLoaded ? (
    <Virtuoso
      ref={virtuoso as Ref<VirtuosoHandle>}
      scrollerRef={enableKeyboardNavigation ? scrollerRef : (_: HTMLElement | Window | null) => {}}
      context={{ currentItemIndex }}
      style={{ width: styleVariables.leftPanel.width, height: '100%', backgroundColor: '#0000003D' }}
      className={sharedClasses.scrollBar}
      totalCount={listItems.length}
      components={{
        Header: () => {
          if (hasZeroResults) {
            return <DifferenceListZeroResultsHeader className={sharedClasses.zeroResults} />;
          }
          return <Grid style={{ marginTop: '16px' }} />;
        },
        Footer: () => <Grid style={{ height: '200px' }} />,
      }}
      itemContent={(index, _, { currentItemIndex: keyboardIndex }) => {
        const item = listItems[index];
        const isKeyboardFocused = keyboardIndex === index;

        if (item.type === ListItemType.CollapsibleHeader) {
          return (
            <Grid
              className={classes.collapsibleHeader}
              onClick={handleToggleNoDifferences}
              data-testid="repeat-no-difference"
            >
              <GVTypography className={classes.collapsibleHeaderText}>
                {`Repeats with No Differences (${item.count})`}
              </GVTypography>
              {isNoDifferencesExpanded ? (
                <KeyboardArrowDownIcon className={classes.chevronIcon} />
              ) : (
                <KeyboardArrowUpIcon className={classes.chevronIcon} />
              )}
            </Grid>
          );
        }

        if (item.type === ListItemType.Subheading) {
          const { repeat, sourcePageNumber } = item;
          const repeatNumber = repeat.index + 1;
          const resultCount = repeat.differencesPostFiltering.length;
          const hasNoDifferences = repeat.differences.length === 0;

          return (
            <Grid className={classes.subheadingContainer} data-testid={`repeat-result-${repeatNumber}`}>
              <GVTypography className={classes.subheadingText}>
                {`Source (P${sourcePageNumber}) v. Repeat ${repeatNumber}`}
              </GVTypography>
              <GVTypography className={classes.resultCountText}>
                {hasNoDifferences ? '0 results' : `${resultCount} result${resultCount === 1 ? '' : 's'}`}
              </GVTypography>
            </Grid>
          );
        }

        if (item.type === ListItemType.NoDifferences) {
          const isSelected = selectedNoDifferenceRepeatIndex === item.repeat.index;
          return (
            <Grid
              className={`${classes.noDifferencesContainer} ${isSelected ? classes.selected : ''}`}
              onClick={() => handleNoDifferenceClick(item.repeat)}
            >
              <GVTypography className={classes.noDifferencesText}>No differences found</GVTypography>
            </Grid>
          );
        }

        // item.index is the shared sequential card number (matches the report).
        const { difference } = item;
        const differenceNumber = item.index;
        const isSelected = selectedDifferenceIds.includes(difference.id);

        return (
          <Grid className={sharedClasses.differenceContainer}>
            <DifferenceElement
              ref={getCardRef(index)}
              key={difference.id}
              numberOfDifference={differenceNumber}
              listIndex={index}
              onClickSelect={onClickSelect}
              difference={difference}
              nextDiffId={
                listItems[index + 1]?.type === ListItemType.Difference
                  ? (listItems[index + 1] as { type: ListItemType.Difference; difference: Difference; index: number })
                      .difference.id
                  : ''
              }
              isSelected={
                currentDifferenceId === difference.id ||
                (Boolean(currentDifferenceGroupId) && currentDifferenceGroupId === difference.groupId)
              }
              isKeyboardFocused={isKeyboardFocused}
              updateCommentOpen={updateCommentOpen}
              isCommentOpen={openComments[difference.id]}
              handleOnStopKeyboardNavigation={handleOnStopKeyboardNavigation}
              multipleSelectionMode={multipleSelectionMode}
              isCheckboxSelected={isSelected}
              onCheckboxChange={onDifferenceSelection}
              customStatuses={inspectionCustomStatuses}
              refetchStatuses={refetchStatuses}
            />
          </Grid>
        );
      }}
    />
  ) : (
    <DifferenceListLoading className={sharedClasses.containerPadding} />
  );
};

export default StepAndRepeatListOfDifferences;
